import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import CollectionCard from "@/components/feature/CollectionCard";
import usePageMeta from "@/hooks/usePageMeta";
import { hotEvents, mustSee, siteInfo } from "@/mocks/guagua";

const ALL = [...hotEvents, ...mustSee];

export default function CollectionDetail() {
  const { id } = useParams<{ id: string }>();
  const item = ALL.find((c) => c.id === id);

  const related = useMemo(() => {
    if (!item) return [];
    const sameCat = ALL.filter((c) => c.categoryId === item.categoryId && c.id !== item.id);
    const others = ALL.filter((c) => c.categoryId !== item.categoryId && c.id !== item.id);
    return [...sameCat, ...others].slice(0, 3);
  }, [item]);

  const jsonLd = useMemo(() => {
    if (!item) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: item.title,
        description: item.summary,
        articleSection: item.categoryName,
        keywords: item.tags.join(","),
        datePublished: `2026-${item.date}`,
        dateModified: `2026-${item.date}`,
        inLanguage: "zh-CN",
        author: { "@type": "Organization", name: siteInfo.name },
        publisher: { "@type": "Organization", name: siteInfo.name },
        mainEntityOfPage: `https://yoovx.cn/collection/${item.id}`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://yoovx.cn/" },
          { "@type": "ListItem", position: 2, name: "合集导航", item: "https://yoovx.cn/collections" },
          { "@type": "ListItem", position: 3, name: item.title, item: `https://yoovx.cn/collection/${item.id}` },
        ],
      },
    ];
  }, [item]);

  usePageMeta({
    title: item ? `${item.title} - 网爆吃瓜合集` : "合集不存在 - 吃瓜合集导航",
    description: item
      ? `${item.summary}。本合集收录于「${item.categoryName}」分类，含话题背景、时间线与看点清单。`
      : "找不到对应的吃瓜合集，返回合集导航重新浏览。",
    keywords: item ? `${item.tags.join(",")},网爆吃瓜,吃瓜合集导航` : "网爆吃瓜,吃瓜合集导航",
    path: item ? `/collection/${item.id}` : "/collections",
    jsonLd,
  });

  if (!item) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl leading-none" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这个合集暂时找不到了</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            链接可能已更新，也可能这个合集已经合并到新的导航页里。
          </p>
          <Link
            to="/collections"
            className="mt-6 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回合集导航
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <article>
          <header className="w-full border-b border-background-200 bg-background-100/60">
            <div className="w-full px-4 py-10 md:px-8 md:py-14">
              <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
                <Link to="/" className="transition-colors hover:text-primary-700">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line leading-none" aria-hidden="true"></i>
                <Link to="/collections" className="transition-colors hover:text-primary-700">
                  合集导航
                </Link>
                <i className="ri-arrow-right-s-line leading-none" aria-hidden="true"></i>
                <Link to={`/collections?cat=${item.categoryId}`} className="transition-colors hover:text-primary-700">
                  {item.categoryName}
                </Link>
              </nav>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                  {item.categoryName}
                </span>
                {item.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-tight text-foreground-950 md:text-5xl">
                {item.title}
              </h1>

              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-600">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-fire-line leading-none text-primary-600" aria-hidden="true"></i>
                  {item.heat} 热度
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-calendar-line leading-none" aria-hidden="true"></i>
                  {item.date} 更新
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-time-line leading-none" aria-hidden="true"></i>
                  约 {item.readTime}读完
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-file-list-3-line leading-none" aria-hidden="true"></i>
                  {item.sections.length} 个章节
                </span>
              </div>
            </div>
          </header>

          <div className="w-full px-4 py-10 md:px-8 md:py-14">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-8">
                <div className="overflow-hidden rounded-lg border border-background-200">
                  <img
                    src={item.cover}
                    alt={`${item.title} 合集封面`}
                    title={`${item.title} - ${item.categoryName}`}
                    className="h-56 w-full object-top md:h-80"
                  />
                </div>

                <p className="mt-8 rounded-lg border-l-4 border-primary-500 bg-primary-50 p-5 text-base leading-relaxed text-foreground-800">
                  {item.summary}
                </p>

                <div className="mt-10 space-y-9">
                  {item.sections.map((section, index) => (
                    <section key={section.heading} id={`chapter-${index}`} className="scroll-mt-24">
                      <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-100 font-heading text-base font-bold text-accent-800">
                          {index + 1}
                        </span>
                        {section.heading}
                      </h2>
                      <p className="mt-3 text-base leading-loose text-foreground-700">{section.body}</p>
                    </section>
                  ))}
                </div>

                {item.points.length > 0 && (
                  <section className="mt-12 rounded-lg border border-secondary-200 bg-secondary-50 p-5 md:p-6">
                    <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                      <i className="ri-list-check-2 leading-none text-secondary-700" aria-hidden="true"></i>
                      看点清单
                    </h2>
                    <ul className="mt-4 space-y-3">
                      {item.points.map((point) => (
                        <li key={point} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                          <i className="ri-checkbox-circle-fill mt-0.5 shrink-0 text-secondary-600 leading-none" aria-hidden="true"></i>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-100/60 p-5">
                  <p className="text-sm text-foreground-600">
                    内容为公开信息的主题化整理，如发现信息有出入，欢迎邮件指正。
                  </p>
                  <a
                    href={`mailto:${siteInfo.email}`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-foreground-950 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-mail-line leading-none" aria-hidden="true"></i>
                    反馈问题
                  </a>
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="sticky top-24 space-y-6">
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                      <i className="ri-list-unordered leading-none text-primary-600" aria-hidden="true"></i>
                      本合集目录
                    </h2>
                    <ul className="mt-4 space-y-2.5">
                      {item.sections.map((section, index) => (
                        <li key={section.heading}>
                          <a
                            href={`#chapter-${index}`}
                            className="flex items-start gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-700"
                          >
                            <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded bg-background-100 text-xs font-semibold text-foreground-600">
                              {index + 1}
                            </span>
                            {section.heading}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                    <h2 className="font-heading text-base font-bold text-foreground-950">相关推荐</h2>
                    <ul className="mt-4 space-y-4">
                      {related.map((rel) => (
                        <li key={rel.id}>
                          <Link to={`/collection/${rel.id}`} className="group flex gap-3">
                            <span className="h-16 w-20 shrink-0 overflow-hidden rounded-md">
                              <img
                                src={rel.cover}
                                alt={`${rel.title} 合集缩略图`}
                                title={`${rel.title} - 相关推荐`}
                                className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                              />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="line-clamp-2 font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
                                {rel.title}
                              </span>
                              <span className="mt-1 block text-xs text-foreground-500">
                                {rel.categoryName} · {rel.readTime}
                              </span>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to={`/collections?cat=${item.categoryId}`}
                    className="flex items-center justify-between rounded-lg bg-foreground-950 px-5 py-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    浏览「{item.categoryName}」全部合集
                    <i className="ri-arrow-right-line leading-none" aria-hidden="true"></i>
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="w-full border-t border-background-200 bg-background-100/60 px-4 py-12 md:px-8 md:py-16">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-3xl">接着看这些合集</h2>
              <Link to="/collections" className="text-sm font-medium text-primary-700 transition-colors hover:text-primary-800">
                全部合集 →
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((rel) => (
                <CollectionCard key={`bottom-${rel.id}`} item={rel} />
              ))}
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}